import React from "react";
import styled from "styled-components";
import { Text } from "@lmn/design-system";
import { OfferRoomDetail } from "@/lib/Types/OfferRoomDetail";
import { MealType } from "@/lib/Types/MealType";
import IconArrowRight from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowRight.js";
import TextButton from "@/components/UI/TextButton/TextButton";
import IncludedServices from "@/components/Stateful/IncludedServices/IncluedServices";
import media from "@/styles/mediaQueries";
import { WhitelabelConfig } from "@/whitelabel/helpers/types";

interface FlexibleOfferData {
  text: string;
  label: string;
}

const Container = styled.div(() => ({
  gridArea: "details",
  display: "flex",
  flexDirection: "column" as const,
}));

const ContentContainer = styled.div(({ theme }) => ({
  border: `1px solid`,
  borderColor: theme.kallisto.color.border.static.borderDisabled,
  padding: theme.kallisto.space.space150,
  borderRadius: theme.kallisto.border.radius.radius100,
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
}));

const ContentTitles = styled.div(() => ({
  display: "flex",
  flexDirection: "row" as const,
  justifyContent: "space-between",
  alignItems: "baseline",
}));

const UpsellingOffersTrigger = styled(TextButton)(() => ({
  marginTop: "1.5rem",
  ...media.screenSmMin({
    marginTop: "auto",
  }),
}));

const Spacer = styled.div(() => ({
  height: "18px",
}));

interface OfferDetailsProps {
  offerRoomDetails: OfferRoomDetail[] | null;
  mealType: MealType | null;
  transfer: boolean;
  rentalCar?: boolean;
  railAndFly: boolean;
  flexibleOfferData?: FlexibleOfferData | null;
  freeRebookingOfferData?: FlexibleOfferData | null;
  bookOnOptionLabel?: string;
  bookOnOptionTooltip?: string;
  oceanView?: string;
  minBaggageAllowance?: { minBags: number; minWeight: number } | null;
  offerForLimitedMobility?: boolean;
  showDetailsLink: boolean;
  onDetailsClick: () => void;
  onUpsellingClick: () => void;
  isFetchingUpsellingOffers: boolean;
  hasNoUpsellingOffers: boolean;
  compactTooltips?: boolean;
  showWegPerks?: boolean;
  WlConfig: WhitelabelConfig;
}

const OfferDetails: React.FC<OfferDetailsProps> = ({
  offerRoomDetails,
  mealType,
  transfer,
  rentalCar,
  railAndFly,
  flexibleOfferData,
  freeRebookingOfferData,
  bookOnOptionLabel,
  bookOnOptionTooltip,
  oceanView,
  minBaggageAllowance,
  offerForLimitedMobility,
  showDetailsLink,
  onDetailsClick,
  onUpsellingClick,
  isFetchingUpsellingOffers,
  hasNoUpsellingOffers,
  compactTooltips,
  showWegPerks,
  WlConfig,
}) => {
  return (
    <Container>
      <ContentTitles>
        <Text variant="Title6" forceTag="h4" margin={{ bottom: "space100" }}>
          Zimmer und Leistungen
        </Text>
        {showDetailsLink && (
          <TextButton onClick={onDetailsClick}>Angebotsdetails</TextButton>
        )}
      </ContentTitles>

      <ContentContainer>
        <IncludedServices
          offerRoomDetails={offerRoomDetails}
          mealType={mealType}
          transfer={transfer}
          rentalCar={rentalCar}
          railAndFly={railAndFly}
          flexibleOfferData={flexibleOfferData}
          freeRebookingOfferData={freeRebookingOfferData}
          bookOnOptionLabel={bookOnOptionLabel}
          bookOnOptionTooltip={bookOnOptionTooltip}
          oceanView={oceanView}
          minBaggageAllowance={minBaggageAllowance}
          offerForLimitedMobility={offerForLimitedMobility}
          showNoTransport
          compactTooltips={compactTooltips}
          showWegPerks={showWegPerks}
          WlConfig={WlConfig}
        />

        {showDetailsLink && (
          <>
            <Spacer />
            <UpsellingOffersTrigger
              variant="action"
              onClick={onUpsellingClick}
              disabled={isFetchingUpsellingOffers || hasNoUpsellingOffers}
              data-testid="upselling-button"
            >
              {isFetchingUpsellingOffers ? (
                <span>wird geprüft…</span>
              ) : hasNoUpsellingOffers ? (
                <span>keine weiteren Leistungen verfügbar</span>
              ) : (
                <>
                  <span>Leistungen anpassen</span> <IconArrowRight size={12} />
                </>
              )}
            </UpsellingOffersTrigger>{" "}
          </>
        )}
      </ContentContainer>
    </Container>
  );
};

export default OfferDetails;
